import { User } from '../../types/api';

import { baseApi } from './baseApi';

export interface GetUserProfileResponse {
  status: 'success';
  message: string;
  data: {
    user: User & {
      referralCount?: number;
      apiKeysCount?: number;
    };
  };
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string | null;
  language?: 'ar' | 'en';
}

export interface UpdateProfileResponse {
  status: 'success';
  message: string;
  data: {
    user: User & {
      referralCount?: number;
      apiKeysCount?: number;
    };
  };
}

export interface LinkTelegramRequest {
  telegramId: number;
}

export interface LinkTelegramResponse {
  status: 'success';
  message: string;
  data: {
    user: User & {
      referralCount?: number;
      apiKeysCount?: number;
    };
  };
}

export interface GetTelegramLinkResponse {
  status: 'success';
  message: string;
  data: {
    linkUrl: string;
  };
}

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get User Profile
    getUserProfile: builder.query<GetUserProfileResponse, void>({
      query: () => ({
        url: '/users/me',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    // Update User Profile
    updateProfile: builder.mutation<UpdateProfileResponse, UpdateProfileRequest>({
      query: (data) => ({
        url: '/users/me',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Link Telegram Account
    linkTelegram: builder.mutation<LinkTelegramResponse, LinkTelegramRequest>({
      query: (data) => ({
        url: '/users/me/link-telegram',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Get Telegram Link
    getTelegramLink: builder.query<GetTelegramLinkResponse, void>({
      query: () => ({
        url: '/users/me/telegram-link',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
  }),
});

// ==================== API Keys Management ====================

export interface ApiKey {
  keyId: string;
  label: string | null;
  createdAt: string;
}

export interface GetApiKeysResponse {
  status: 'success';
  message: string;
  data: {
    keys: ApiKey[];
  };
}

export interface CreateApiKeyRequest {
  label?: string;
}

export interface CreateApiKeyResponse {
  status: 'success';
  message: string;
  data: {
    keyId: string;
    apiKey: string; // Full key in format: keyId.rawKey (only returned once)
  };
}

export interface DeleteApiKeyResponse {
  status: 'success';
  message: string;
}

export const apiKeysApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // List API Keys
    getApiKeys: builder.query<GetApiKeysResponse, void>({
      query: () => ({
        url: '/keys',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    // Create API Key
    createApiKey: builder.mutation<CreateApiKeyResponse, CreateApiKeyRequest>({
      query: (data) => ({
        url: '/keys',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Delete API Key
    deleteApiKey: builder.mutation<DeleteApiKeyResponse, string>({
      query: (keyId) => ({
        url: `/keys/${keyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

// ==================== Webhook Management ====================

export interface GetWebhookUrlResponse {
  status: 'success';
  message: string;
  data: {
    webhookUrl: string | null;
  };
}

export interface SetWebhookUrlRequest {
  webhookUrl: string | null;
}

export interface SetWebhookUrlResponse {
  status: 'success';
  message: string;
  data: {
    user: User & {
      referralCount?: number;
      apiKeysCount?: number;
    };
  };
}

export interface DeleteWebhookUrlResponse {
  status: 'success';
  message: string;
  data: {
    user: User & {
      referralCount?: number;
      apiKeysCount?: number;
    };
  };
}

export const webhookApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Get Webhook URL
    getWebhookUrl: builder.query<GetWebhookUrlResponse, void>({
      query: () => ({
        url: '/users/me/webhook',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    // Set/Update Webhook URL
    setWebhookUrl: builder.mutation<SetWebhookUrlResponse, SetWebhookUrlRequest>({
      query: (data) => ({
        url: '/users/me/webhook',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),

    // Delete Webhook URL
    deleteWebhookUrl: builder.mutation<DeleteWebhookUrlResponse, void>({
      query: () => ({
        url: '/users/me/webhook',
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useGetUserProfileQuery,
  useUpdateProfileMutation,
  useLinkTelegramMutation,
  useGetTelegramLinkQuery,
} = userApi;

export const {
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
} = apiKeysApi;

export const {
  useGetWebhookUrlQuery,
  useSetWebhookUrlMutation,
  useDeleteWebhookUrlMutation,
} = webhookApi;

